/**
 * Cart Tool Generator — detects cart-related flows by pattern matching
 * flow names and state paths, produces add/remove/get/update cart tools.
 */

import type { CogoBlockDef, CogoFlow } from "@cogo/types"
import type { WebMCPTool } from "../core/tool-registry"
import type { GeneratorRuntime } from "./form-tools"

// ── Pattern matchers ──────────────────────────────────────

const CART_ADD_RE = /add.*cart|cart.*add/i
const CART_REMOVE_RE = /remove.*cart|cart.*remove|remove.*item/i
const CART_UPDATE_RE = /update.*cart.*quant|cart.*update|update.*quantity/i
const CART_CLEAR_RE = /clear.*cart/i

type CartFlowType = "add" | "remove" | "update" | "clear"

interface DetectedCartFlow {
  flowName: string
  type: CartFlowType
}

function classifyCartFlow(flowName: string): CartFlowType | null {
  if (CART_ADD_RE.test(flowName)) return "add"
  if (CART_REMOVE_RE.test(flowName)) return "remove"
  if (CART_UPDATE_RE.test(flowName)) return "update"
  if (CART_CLEAR_RE.test(flowName)) return "clear"
  return null
}

// ── Cart state detection ──────────────────────────────────

function detectCartStatePath(block: CogoBlockDef): string | null {
  // Walk UI tree looking for iterateOver or bind containing "cart"
  function walk(el: { iterateOver?: string; bind?: string; children?: unknown[] }): string | null {
    if (el.iterateOver && /cart/i.test(el.iterateOver)) {
      // #{appData.cart.items} → appData.cart.items
      return el.iterateOver.replace(/^#\{/, "").replace(/\}$/, "")
    }
    if (el.bind && /cart/i.test(el.bind)) {
      return el.bind.replace(/^#\{/, "").replace(/\}$/, "")
    }
    for (const child of (el.children ?? []) as Array<{ iterateOver?: string; bind?: string; children?: unknown[] }>) {
      const found = walk(child)
      if (found) return found
    }
    return null
  }

  if (!block.root) return null
  return walk(block.root as unknown as { iterateOver?: string; bind?: string; children?: unknown[] })
}

// ── Generator ─────────────────────────────────────────────

export function generateCartTools(
  block: CogoBlockDef,
  flows: Record<string, CogoFlow>,
): WebMCPTool[] {
  // Detect cart flows
  const detected: DetectedCartFlow[] = []
  for (const flowName of Object.keys(flows)) {
    const type = classifyCartFlow(flowName)
    if (type) detected.push({ flowName, type })
  }

  if (detected.length === 0) return []

  const cartStatePath = detectCartStatePath(block)
  const tools: WebMCPTool[] = []

  // add_to_cart
  const addFlow = detected.find(d => d.type === "add")
  if (addFlow) {
    tools.push({
      name: "add_to_cart",
      description: flows[addFlow.flowName]?.narrative?.effect ?? `Add a product to the shopping cart via flow "${addFlow.flowName}"`,
      inputSchema: {
        type: "object",
        properties: {
          productId: { type: "string", description: "Product ID to add" },
          quantity: { type: "string", description: "Quantity to add (default: 1)" },
        },
        required: ["productId"],
      },
      handler: createCartFlowHandler(addFlow.flowName, (params) => ({
        productId: params.productId as string,
        quantity: (params.quantity as string) ?? "1",
      })),
      metadata: {
        blockName: block.blockType,
        tags: ["cart", "shopping"],
      },
    })
  }

  // remove_from_cart
  const removeFlow = detected.find(d => d.type === "remove")
  if (removeFlow) {
    tools.push({
      name: "remove_from_cart",
      description: flows[removeFlow.flowName]?.narrative?.effect ?? `Remove a product from the shopping cart via flow "${removeFlow.flowName}"`,
      inputSchema: {
        type: "object",
        properties: {
          productId: { type: "string", description: "Product ID to remove" },
        },
        required: ["productId"],
      },
      handler: createCartFlowHandler(removeFlow.flowName, (params) => ({
        productId: params.productId as string,
      })),
      metadata: {
        blockName: block.blockType,
        tags: ["cart", "shopping"],
      },
    })
  }

  // get_cart_contents (always available if we detected any cart flows)
  tools.push({
    name: "get_cart_contents",
    // TODO: thread narrative from caller — get_cart_contents has no associated flow
    description: "Get current cart contents" + (cartStatePath ? ` from state path "${cartStatePath}"` : ""),
    inputSchema: { type: "object", properties: {} },
    handler: createGetCartHandler(cartStatePath ?? "appData.cart"),
    metadata: {
      blockName: block.blockType,
      readOnly: true,
      tags: ["cart", "shopping"],
    },
  })

  // update_cart_quantity
  const updateFlow = detected.find(d => d.type === "update")
  if (updateFlow) {
    tools.push({
      name: "update_cart_quantity",
      description: flows[updateFlow.flowName]?.narrative?.effect ?? `Update cart item quantity via flow "${updateFlow.flowName}"`,
      inputSchema: {
        type: "object",
        properties: {
          productId: { type: "string", description: "Product ID to update" },
          quantity: { type: "string", description: "New quantity" },
        },
        required: ["productId", "quantity"],
      },
      handler: createCartFlowHandler(updateFlow.flowName, (params) => ({
        productId: params.productId as string,
        quantity: params.quantity as string,
      })),
      metadata: {
        blockName: block.blockType,
        tags: ["cart", "shopping"],
      },
    })
  }

  return tools
}

// ── Handlers ──────────────────────────────────────────────

function createCartFlowHandler(
  flowName: string,
  mapParams: (params: Record<string, unknown>) => Record<string, unknown>,
): (params: Record<string, unknown>) => (runtime: GeneratorRuntime) => Promise<unknown> {
  return (params: Record<string, unknown>) => {
    return async (runtime: GeneratorRuntime) => {
      const mapped = mapParams(params)
      return runtime.actionEngine.executeFlow(flowName, mapped)
    }
  }
}

function createGetCartHandler(
  cartStatePath: string,
): (params: Record<string, unknown>) => (runtime: GeneratorRuntime) => Promise<unknown> {
  return (_params: Record<string, unknown>) => {
    return async (runtime: GeneratorRuntime) => {
      return runtime.stateManager.get(cartStatePath)
    }
  }
}
